import _ from 'underscore';
import { getAppStore } from '../../../app/AppStore';
import { RequestHelper } from '../../../httpClient/utils/RequestHelper';
import { ResultHelper } from '../../../httpClient/utils/ResultHelper';
import {
  CreateOrUpdateSiteOIDCConfigurationRequest,
} from '../../../ts/ServerApi/requests/CreateOrUpdateSiteOIDCConfigurationRequest';
import {
  GetOrCreateSiteOIDCConfigurationViewModelRequest,
  ISiteOIDCConfigurationViewModel,
} from '../../../ts/ServerApi/requests/GetOrCreateSiteOIDCConfigurationViewModelRequest';
import { GetSiteSAMLConfigurationRequest } from '../../../ts/ServerApi/requests/GetSiteSAMLConfigurationRequest';
import { RemoveIdentityProviderRequest } from '../../../ts/ServerApi/requests/RemoveIdentityProviderRequest';
import { SetSiteAttributeMappingRequest } from '../../../ts/ServerApi/requests/SetSiteAttributeMapping';
import {
  SetSiteSAMLAllowEmbeddedAuthenticationRequest,
} from '../../../ts/ServerApi/requests/SetSiteSAMLAllowEmbeddedAuthenticationRequest';
import {
  SetSiteSAMLAuthSettingForEmbeddedViewsRequest,
} from '../../../ts/ServerApi/requests/SetSiteSAMLAuthSettingForEmbeddedViewsRequest';
import { SetSiteSAMLEnabledRequest } from '../../../ts/ServerApi/requests/SetSiteSAMLEnabledRequest';
import { UpdateUserAuthSettingsRequest } from '../../../ts/ServerApi/requests/UpdateUserAuthSettingsRequest';
import { IResult } from '../../../httpClient/serverApi/types/IResponse';
import { ISiteSAMLConfiguration } from '../../../ts/ServerApi/types/ISiteSAMLConfiguration';
import { SiteUserAuthSetting } from '../../../ts/ServerApi/types/ISiteUser';
import {
  getSessionManagementServiceInstance,
} from '../../../sessionManagement/SessionManagementService';
import { ServerSettingsUnauthenticated } from '../../services/ServerSettingsUnauthenticated';
import WebHttpClient from '../../../httpClient/WebHttpClient';
import { AuthenticationTypeResolutionService, SiteDefaultAuth } from '~src/authentication/services/AuthenticationTypeResolutionService';
import { Messages } from '../../../codegen/Localize';
import { cloneDeep } from 'lodash';

export interface IAuthenticationAttributeMapping {
  emailMapping: string;
  firstNameMapping: string;
  lastNameMapping: string;
  fullNameMapping: string;
  useFullName: boolean;
}

export interface IAuthenticationProvider {
  allowEmbeddedAuthentication: boolean;
  id: string;
  isEditable: boolean;
  isEnabled: boolean;
  isLogoutSupported: boolean;
  testLoginUrl: string;
  type: string;
}

export const AuthenticationProviderType = {
  default: 'DEFAULT',
  oidc: 'OIDC',
  saml: 'SAML',
  tabidmfa: 'TABID_WITH_MFA',
};

export interface IOIDCAuthenticationProvider extends IAuthenticationProvider {
  alias: string;
  salesforceDomain: string;
}

export interface ISAMLAuthenticationProvider extends IAuthenticationProvider {
  acsUrl: string;
  attributeMapping: IAuthenticationAttributeMapping;
  certificateUrl: string;
  identityProviderEntityId: string;
  serviceProviderEntityId: string;
  ssoServiceURL: string;
}

export interface ISiteAuthenticationSettings {
  defaultProvider: IAuthenticationProvider;
  providerIdForEmbeddedViews: string; // null means: "let the user decide which provider to use"
  externalProviders: IAuthenticationProvider[];
  tabIdMfaProvider: IAuthenticationProvider;

}

interface ISiteAuthenticationSettingsInternal extends ISiteAuthenticationSettings {
  samlConfiguration: ISiteSAMLConfiguration;
  oidcViewModel: ISiteOIDCConfigurationViewModel;
}

const DefaultAuthenticationProviderId = 'DEFAULT';
export const DefaultAuthenticationProvider: IAuthenticationProvider = {
  allowEmbeddedAuthentication: false,
  id: DefaultAuthenticationProviderId,
  isEditable: false,
  isEnabled: true,
  isLogoutSupported: true,
  testLoginUrl: null,
  type: AuthenticationProviderType.default,
};

const TabIdWithMFAAuthenticationProviderId = 'TABID_WITH_MFA';
export const TabIdWithMFAAuthenticationProvider: IAuthenticationProvider = {
  allowEmbeddedAuthentication: false,
  id: TabIdWithMFAAuthenticationProviderId,
  isEditable: false,
  isEnabled: false,
  isLogoutSupported: true,
  testLoginUrl: null,
  type: AuthenticationProviderType.tabidmfa,
};

export const AuthenticationSettingsWhenDisabled: ISiteAuthenticationSettings = {
  defaultProvider: DefaultAuthenticationProvider,
  providerIdForEmbeddedViews: null,
  externalProviders: [],
  tabIdMfaProvider: null
};

export function is3rdParty(authenticationProviderId: string): boolean {
  return authenticationProviderId !== DefaultAuthenticationProviderId && authenticationProviderId !== TabIdWithMFAAuthenticationProviderId;
}

// shim needed to translate between the new SiteAuthenticationSettings client API above and the existing SAML/OIDC server APIs
export namespace SiteAuthenticationSettingsService {

  let siteId: string;
  let siteAuthenticationSettingsInternalPromise: Promise<ISiteAuthenticationSettingsInternal>;

  export function isEnabled(): boolean {
    return ServerSettingsUnauthenticated.isSiteSamlEnabled();
  }

  export function getSiteAuthenticationSettings(refresh: boolean = false): Promise<ISiteAuthenticationSettings> {
    if (isEnabled()) {
      return getSiteAuthenticationSettingsInternal(refresh);
    } else {
      return Promise.resolve(AuthenticationSettingsWhenDisabled);
    }
  }

  export function getDefaultProvider(): IAuthenticationProvider {
    return DefaultAuthenticationProvider;
  }

  export function getTabIdMFAProvider(): IAuthenticationProvider {
    return TabIdWithMFAAuthenticationProvider;
  }

  export function enableProvider(id: string): Promise<void> {
    throwUnlessEnabled();
    return getSiteAuthenticationSettingsInternal().then((settings: ISiteAuthenticationSettingsInternal) => {
      const newSettings = cloneDeep(settings);
      const provider = this.findProvider(newSettings, id);
      let noChanges = true;

      _.each(newSettings.externalProviders, (externalProvider: IAuthenticationProvider) => {
        const shouldBeEnabled = (externalProvider.id === id);
        noChanges = noChanges && (externalProvider.isEnabled === shouldBeEnabled);
        externalProvider.isEnabled = shouldBeEnabled;
      });
      if (noChanges) {
        return Promise.resolve();
      }
      switch(provider.type) {
        case AuthenticationProviderType.saml:
          newSettings.samlConfiguration.enabled = true;
          newSettings.oidcViewModel.configuration.enabled = false;
          break;
        case AuthenticationProviderType.oidc:
          newSettings.samlConfiguration.enabled = false;
          newSettings.oidcViewModel.configuration.enabled = true;
          newSettings.oidcViewModel.configuration.knownProviderAlias = (<IOIDCAuthenticationProvider>provider).alias;
          newSettings.oidcViewModel.configuration.salesforceDomain = (<IOIDCAuthenticationProvider>provider).salesforceDomain;
          break;
        case AuthenticationProviderType.default:
          newSettings.samlConfiguration.enabled = false;
          newSettings.oidcViewModel.configuration.enabled = false;
          break;
        default:
          throw new Error(`Unsupported provider type ${provider.type}`);
      }
      return Promise.all([
        WebHttpClient.sendRequest(new SetSiteSAMLEnabledRequest({ enable: newSettings.samlConfiguration.enabled})),
        WebHttpClient.sendRequest(new CreateOrUpdateSiteOIDCConfigurationRequest({ siteOIDCConfiguration: newSettings.oidcViewModel.configuration }))
      ]).then(() => {
        Object.assign(settings, newSettings);
      });
    });
  }

  export function handleSAMLConfigurationUpdate(samlConfiguration: ISiteSAMLConfiguration): Promise<boolean> {
    return getSiteAuthenticationSettingsInternal().then((settings: ISiteAuthenticationSettingsInternal) => {
      settings.samlConfiguration = samlConfiguration;
      const updatedProvider = getSAMLProvider(samlConfiguration);
      const provider = findProvider(settings, updatedProvider.id);
      Object.assign(provider, updatedProvider);
      return true;
    });
  }

  export function removeSAMLMetadata(id: string): Promise<boolean> {
    throwUnlessEnabled();
    return WebHttpClient.sendRequest(new RemoveIdentityProviderRequest()).then((samlConfiguration: ISiteSAMLConfiguration) => {
      return handleSAMLConfigurationUpdate(samlConfiguration);
    });
  }

  export function uploadSAMLMetadata(id: string, samlMetadata: File): Promise<boolean> {
    throwUnlessEnabled();
    return getSessionManagementServiceInstance().uploadSAMLMetadata(samlMetadata).then((samlConfiguration: ISiteSAMLConfiguration) => {
      return handleSAMLConfigurationUpdate(samlConfiguration);
    });
  }

  export function setSAMLAttributeMapping(id: string, attributeMapping: IAuthenticationAttributeMapping): Promise<boolean> {
    throwUnlessEnabled();
    return WebHttpClient.sendRequest(new SetSiteAttributeMappingRequest(attributeMapping)).then(() => {
      return WebHttpClient.sendRequest(new GetSiteSAMLConfigurationRequest());
    }).then((samlConfiguration: ISiteSAMLConfiguration) => {
      return handleSAMLConfigurationUpdate(samlConfiguration);
    });
  }

  // should be removed by TFSID:607630
  export function getAuthSetting(id: string, settings: ISiteAuthenticationSettings): string {
    const provider = findProvider(settings, id);
    if (!provider) {
      throw new Error(`Provider with id ${id} doesn't exist`);
    }
    switch (provider.type) {
      case AuthenticationProviderType.saml:
        return SiteUserAuthSetting.Saml;
      case AuthenticationProviderType.oidc:
        return SiteUserAuthSetting.Oidc;
      case AuthenticationProviderType.default:
        return SiteUserAuthSetting.Default;
      case AuthenticationProviderType.tabidmfa:
        return SiteUserAuthSetting.TabId_with_MFA;
      default:
        throw new Error(`Unsupported provider type ${provider.type}`);
    }
  }

  export function setProviderIdForEmbeddedViews(id: string): Promise<void> {
    throwUnlessEnabled();
    return getSiteAuthenticationSettings().then((settings: ISiteAuthenticationSettings) => {
      const authSetting = (id ? getAuthSetting(id, settings) : null);
      return WebHttpClient.sendRequest(new SetSiteSAMLAuthSettingForEmbeddedViewsRequest({authSetting: authSetting})).then(() => {
        settings.providerIdForEmbeddedViews = id;
      });
    });
  }

  export function setProviderIdForUsers(id: string, userIds: string[]): Promise<IResult> {
    throwUnlessEnabled();
    return getSiteAuthenticationSettings().then((settings: ISiteAuthenticationSettings) => {
      const authSetting = getAuthSetting(id, settings);
      const requests = RequestHelper.buildRequests(userIds, (userIds => new UpdateUserAuthSettingsRequest(userIds, authSetting)));
      return WebHttpClient.sendRequestsParallel(requests, ResultHelper.combineResults);
    });
  }

  export function setAllowEmbeddedAuthentication(id: string, val: boolean): Promise<boolean> {
    throwUnlessEnabled();
    return WebHttpClient.sendRequest(new SetSiteSAMLAllowEmbeddedAuthenticationRequest({allow: val})).then((samlConfiguration: ISiteSAMLConfiguration) => {
      return handleSAMLConfigurationUpdate(samlConfiguration);
    });
  }

  export function getProviderDisplayName(provider: IAuthenticationProvider): string {
    switch (provider.type) {
      case AuthenticationProviderType.oidc:
        return getDisplayNameForOIDCProvider(<IOIDCAuthenticationProvider>provider);
      case AuthenticationProviderType.saml:
        return getDisplayNameForSAMLProvider(<ISAMLAuthenticationProvider>provider);
      case AuthenticationProviderType.default:
        return getDisplayNameForDefaultProvider(provider);
      case AuthenticationProviderType.tabidmfa:
        return getDisplayNameForTOLWithMFAProvider();
      default:
        throw new Error(`Unsupported provider type ${provider.type}`);
    }
  }

  function getDisplayNameForTOLWithMFAProvider(): string {
    return Messages.ssoAuthTypeDefault_name_tableauid_mfa();
  }

  export function findProvider(settings: ISiteAuthenticationSettings, id: string) {
    if (id === settings.defaultProvider.id) {
      return settings.defaultProvider;
    }
    if (id === settings.tabIdMfaProvider.id) {
      return settings.tabIdMfaProvider;
    }
    return _.find(settings.externalProviders, (provider: IAuthenticationProvider) => (provider.id === id));
  }

  export function isAuthenticationSSO(settings: ISiteAuthenticationSettings) {
    return settings && settings.externalProviders && !!_.find(settings.externalProviders, (provider: IAuthenticationProvider) => (provider.isEnabled));
  }

  // should be removed by TFSID:607630
  export function getAuthSettingToProviderIdMap(): Promise<_.Dictionary<string>> {
    if (isEnabled()) {
      return getSiteAuthenticationSettingsInternal().then((settings: ISiteAuthenticationSettingsInternal) => {
        return getAuthSettingToProviderIdMapInternal(settings.oidcViewModel);
      });
    } else {
      const map = {
        [SiteUserAuthSetting.Default]: DefaultAuthenticationProvider.id
      };
      return Promise.resolve(map);
    }
  }

  export function saveOIDCProvider(provider: IOIDCAuthenticationProvider): Promise<IResult> {
    return WebHttpClient.sendRequest(new CreateOrUpdateSiteOIDCConfigurationRequest({siteOIDCConfiguration: {
      enabled: provider.isEnabled,
      testLoginUrl: provider.testLoginUrl,
      knownProviderAlias: provider.alias,
      salesforceDomain: provider.salesforceDomain,
    }}));
  }

  function throwUnlessEnabled() {
    if (!isEnabled()) {
      throw new Error('Site authentication settings are not enabled');
    }
  }

  function getSiteAuthenticationSettingsInternal(refresh: boolean = false): Promise<ISiteAuthenticationSettingsInternal> {
    // Since only React components may subscribe to the app store, manually refresh to check for change before sending requests
    checkIfAppStateChanged();

    if (!siteAuthenticationSettingsInternalPromise || refresh) {
      siteAuthenticationSettingsInternalPromise = getSiteAuthenticationSettingsInternalImpl();
    }
    return siteAuthenticationSettingsInternalPromise;
  }

  function getAuthSettingToProviderIdMapInternal(oidcViewModel: ISiteOIDCConfigurationViewModel): _.Dictionary<string> {
    // keys: siteUserAuthSetting, values: authentication provider id
    const map = {
      [SiteUserAuthSetting.Oidc]: getOIDCProviderId(oidcViewModel.configuration.knownProviderAlias),
      [SiteUserAuthSetting.Saml]: getSAMLProviderId(),
      [SiteUserAuthSetting.Default]: DefaultAuthenticationProvider.id,
      [SiteUserAuthSetting.TabId_with_MFA]: TabIdWithMFAAuthenticationProvider.id
    };
    return map;
  }

  function getSiteAuthenticationSettingsInternalImpl(): Promise<ISiteAuthenticationSettingsInternal> {
    return Promise.all([
      WebHttpClient.sendRequest(new GetSiteSAMLConfigurationRequest()).catch(() => {
        /* Don't fail if the user has no permission */
        return {};
      }),
      WebHttpClient.sendRequest(new GetOrCreateSiteOIDCConfigurationViewModelRequest())
    ]).then((results: any[]) => {
      const samlConfiguration: ISiteSAMLConfiguration = results[0];
      const oidcViewModel: ISiteOIDCConfigurationViewModel = results[1];

      const externalProviders: IAuthenticationProvider[] = getBuiltinOIDCProviders(oidcViewModel);
      const samlProvider: ISAMLAuthenticationProvider = getSAMLProvider(samlConfiguration);
      externalProviders.push(samlProvider);

      const tabIdMfaProvider = TabIdWithMFAAuthenticationProvider;
      if (getSessionManagementServiceInstance().isMfaEnabled()) {
        tabIdMfaProvider.isEnabled = true;
      }
      else {
        tabIdMfaProvider.isEnabled = false;
      }
      const authSettingToProviderIdMap = getAuthSettingToProviderIdMapInternal(oidcViewModel);

      return {
        defaultProvider: DefaultAuthenticationProvider,
        externalProviders: externalProviders,
        tabIdMfaProvider: tabIdMfaProvider,
        providerIdForEmbeddedViews: authSettingToProviderIdMap[samlConfiguration.authSettingForEmbeddedViews],
        samlConfiguration: samlConfiguration,
        oidcViewModel: oidcViewModel,
      };
    });
  }

  function getOIDCProviderId(alias: string) {
    return AuthenticationProviderType.oidc + ':' + alias;
  }

  function getBuiltinOIDCProviders(oidcViewModel: ISiteOIDCConfigurationViewModel): IOIDCAuthenticationProvider[] {
    const oidcConfiguration = oidcViewModel.configuration;
    return _.map(oidcViewModel.knownProviderAliases, (knownProviderAlias: string) => {
      return {
        allowEmbeddedAuthentication: false,
        id: getOIDCProviderId(knownProviderAlias),
        isEditable: false,
        isEnabled: oidcConfiguration.enabled && oidcConfiguration.knownProviderAlias === knownProviderAlias,
        isLogoutSupported: true,
        testLoginUrl: oidcConfiguration.testLoginUrl,
        type: AuthenticationProviderType.oidc,

        alias: knownProviderAlias,
        salesforceDomain: oidcConfiguration.salesforceDomain,
      };
    });
  }

  function getSAMLProviderId() {
    return AuthenticationProviderType.saml;
  }

  function getSAMLProvider(samlConfiguration: ISiteSAMLConfiguration): ISAMLAuthenticationProvider {
    return {
      allowEmbeddedAuthentication: samlConfiguration.allowEmbeddedAuthentication,
      id: getSAMLProviderId(),
      isEditable: true,
      isEnabled: samlConfiguration.enabled,
      isLogoutSupported: samlConfiguration.isSingleLogoutSupported,
      testLoginUrl: samlConfiguration.testLoginUrl,
      type: AuthenticationProviderType.saml,

      acsUrl: samlConfiguration.acsUrl,
      attributeMapping: samlConfiguration.siteSAMLAttributeMapping,
      certificateUrl: samlConfiguration.certificateUrl,
      identityProviderEntityId: samlConfiguration.identityProviderEntityId,
      serviceProviderEntityId: samlConfiguration.serviceProviderEntityId,
      ssoServiceURL: samlConfiguration.ssoServiceURL,
    };
  }

  function getDisplayNameForSAMLProvider(provider: ISAMLAuthenticationProvider): string {
    const providerName = maybeGetHost(provider.ssoServiceURL);
    const translateKeyFn = providerName ? Messages.ssoAuthTypeSAML_name : Messages.ssoAuthTypeSAML_name_undefined;
    const translateValues = { PROVIDER_NAME: providerName };
    return translateKeyFn(translateValues);
  }

  function getDisplayNameForOIDCProvider(provider: IOIDCAuthenticationProvider): string {
    const translateKeyFn = provider.isEditable ? Messages.ssoAuthTypeOIDC_name_editable : Messages.ssoAuthTypeOIDC_name;
    const translateValues = { PROVIDER_NAME: provider.alias };
    return translateKeyFn(translateValues);
  }

  function getDisplayNameForDefaultProvider(provider: IAuthenticationProvider): string {
    let translatedDisplayName;
    if (ServerSettingsUnauthenticated.isTableauIdSupported()) {
      translatedDisplayName = Messages.ssoAuthTypeDefault_name_tableauid();
    } else {
      switch (AuthenticationTypeResolutionService.siteAuthDefault()) {
        case SiteDefaultAuth[SiteDefaultAuth.Local]:
          translatedDisplayName = Messages.ssoAuthTypeDefault_name_local();
          break;
        case SiteDefaultAuth[SiteDefaultAuth.ActiveDirectory]:
          translatedDisplayName = Messages.ssoAuthTypeDefault_name_ad();
          break;
        case SiteDefaultAuth[SiteDefaultAuth.SAML]:
          translatedDisplayName = Messages.ssoAuthTypeDefault_name_saml();
          break;
        case SiteDefaultAuth[SiteDefaultAuth.External]:
          translatedDisplayName = Messages.ssoAuthTypeDefault_name_external();
          break;
        default:
          translatedDisplayName = Messages.ssoAuthTypeDefault_name_other();
          break;
      }
    }
    return translatedDisplayName;
  }

  function maybeGetHost(urlString: string): string {
    const match = /\/\/.*?(\w+\.\w+)($|:|\/)/.exec(urlString);
    if (match) {
      return match[1];
    }
    return undefined;
  }

  function checkIfAppStateChanged() {
    const state = getAppStore().getState();
    const sessionSiteId = state && state.session && state.session.info && state.session.info.site.id;
    if (siteId !== sessionSiteId) {
      siteId = sessionSiteId;
      siteAuthenticationSettingsInternalPromise = null;
    }
  }
}

export type SiteAuthenticationSettingsService = typeof SiteAuthenticationSettingsService;
